import { useEffect, useState, type FormEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { KeyValueFields } from '@/components/ui/KeyValueFields';
import { Modal } from '@/components/ui/Modal';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  formErrorsFromFields,
  type FormErrors,
} from '@/lib/form-errors';
import {
  emptyKeyValueRow,
  recordFromKeyValueRows,
  type KeyValueRow,
} from '@/lib/key-value';
import type { ChannelCredentials } from '@/types/integrations';
import type { ChannelSpec } from './integrations-catalog';
import { useConnectChannel } from './use-integrations';

/**
 * Conectar (o reconectar) un canal a mano: `PUT .../integrations/channels/:channel` (ADMIN).
 *
 * Los campos salen de `integrations-catalog.ts`, que es espejo de la validación real del
 * backend: si falta un campo obligatorio, el backend responde 400 con `details` (y el mensaje
 * cae en el aviso general, porque el detalle de Zod llega agrupado bajo `body`; ver
 * `src/lib/form-errors.ts`). Por eso la obligatoriedad se comprueba también aquí, campo por
 * campo.
 *
 * Las credenciales guardadas NUNCA se devuelven (se cifran con AES-256-GCM): reconectar
 * significa volver a escribirlas.
 */

export interface ChannelCredentialsDialogProps {
  open: boolean;
  onClose: () => void;
  spec: ChannelSpec;
  /** Si el canal ya estaba conectado (cambia los textos de la ayuda). */
  configured: boolean;
}

export function ChannelCredentialsDialog({
  open,
  onClose,
  spec,
  configured,
}: ChannelCredentialsDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const connectChannel = useConnectChannel(tenantId);

  const [values, setValues] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<KeyValueRow[]>([emptyKeyValueRow()]);
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /** Cada apertura arranca en blanco (no se puede precargar lo guardado). */
  useEffect(() => {
    if (!open) return;

    setValues({});
    setRows([emptyKeyValueRow()]);
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
  }, [open, spec.channel]);

  async function send(credentials: ChannelCredentials) {
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      await connectChannel.mutateAsync({ channel: spec.channel, credenciales: credentials });
      onClose();
    } catch (error) {
      const fields = spec.fields.map((field) => field.key);
      setErrors(formErrorsFromApiError(error, [...fields, 'credenciales']));
      setSubmitError(getErrorMessage(error));
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (spec.freeFormCredentials) {
      const record = recordFromKeyValueRows(rows);
      if (!record) {
        setErrors(formErrorsFromFields({ credenciales: 'Agrega al menos una credencial' }));
        setSubmitError(null);
        return;
      }

      await send(record);
      return;
    }

    const missing = formErrorsFromFields(
      Object.fromEntries(
        spec.fields
          .filter((field) => field.required)
          .map((field) => [
            field.key,
            values[field.key]?.trim() ? undefined : `\`${field.label}\` es obligatorio`,
          ]),
      ),
    );

    if (Object.keys(missing.fieldErrors).length > 0) {
      setErrors(missing);
      setSubmitError(null);
      return;
    }

    const credentials: ChannelCredentials = {};
    for (const field of spec.fields) {
      const value = values[field.key]?.trim() ?? '';
      if (value.length > 0) credentials[field.key] = value;
    }

    await send(credentials);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`${configured ? 'Reconfigurar' : 'Conectar'} ${spec.label}`}
      description="Las credenciales se validan y se guardan cifradas (AES-256-GCM) asociadas solo a este negocio."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={connectChannel.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="channel-credentials-form" loading={connectChannel.isPending}>
            {configured ? 'Guardar credenciales' : 'Conectar canal'}
          </Button>
        </>
      }
    >
      <form id="channel-credentials-form" onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorAlert message={submitError} errors={errors} />

        {configured ? (
          <Alert tone="info">
            Este canal ya estaba conectado. El backend no devuelve las credenciales guardadas
            (están cifradas), así que al reconectar hay que escribirlas completas.
          </Alert>
        ) : null}

        {spec.freeFormCredentials ? (
          <KeyValueFields
            rows={rows}
            onChange={setRows}
            keyLabel="Credencial"
            valueLabel="Valor"
            addLabel="Agregar credencial"
            keyPlaceholder="ej. app_id"
            valuePlaceholder="valor"
          />
        ) : (
          spec.fields.map((field) => (
            <Field
              key={field.key}
              label={field.label}
              htmlFor={`channel-${spec.channel}-${field.key}`}
              required={field.required}
              hint={field.hint}
              error={fieldError(errors, field.key)}
            >
              {field.secret ? (
                <PasswordInput
                  id={`channel-${spec.channel}-${field.key}`}
                  name={field.key}
                  autoComplete="off"
                  placeholder={field.placeholder}
                  value={values[field.key] ?? ''}
                  invalid={Boolean(fieldError(errors, field.key))}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                />
              ) : (
                <Input
                  id={`channel-${spec.channel}-${field.key}`}
                  name={field.key}
                  autoComplete="off"
                  placeholder={field.placeholder}
                  value={values[field.key] ?? ''}
                  invalid={Boolean(fieldError(errors, field.key))}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                />
              )}
            </Field>
          ))
        )}

        {spec.note ? <Alert tone="warning">{spec.note}</Alert> : null}

        <p className="text-micro text-ink-faint">
          Al guardar, el backend valida las credenciales del canal y marca el canal como activo.
          Después puedes usar «Probar conexión» para comprobar contra Meta.
        </p>
      </form>
    </Modal>
  );
}
